// AccountIndexScreen

import { Link, type LinkProps } from "expo-router"
import ExpoConstants from "expo-constants"
import * as Linking from "expo-linking"
import * as Updates from "expo-updates"
import { updateId } from "expo-updates"
import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetView,
} from "@gorhom/bottom-sheet"
import { TouchableOpacity, View } from "react-native"
import { BFColors } from "@/constants/Colors"

import ChevronRight from "@/assets/icons/chevron-right.svg"
import { useCallback, useRef, useState } from "react"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import {
  useDeleteUserAccountMutation,
  useLogout,
  useUser,
} from "@/queries/user"
import FeedbackTextInput from "@/components/input/FeedbackTextInput"
import * as AccountEvents from "@/analytics/events/account"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import { Body, H2, H3, Small, Subtitle } from "@/components/standard/BFText"
import { BFScreen } from "@/components/standard/screen/BFScreen"
import { $authToken } from "@/stores/auth"
import config from "@/config"

type MenuItemProps = LinkProps & {
  menuItemValue: string
  danger?: boolean
}

const MenuItemLink: React.FC<MenuItemProps> = ({
  menuItemValue,
  danger = false,
  disabled,
  ...props
}: MenuItemProps) => {
  return (
    <View className="px-5 py-6 border-b border-light-grey">
      <Link disabled={disabled} {...props} asChild>
        <TouchableOpacity
          testID={`button-${menuItemValue}`}
          className={`flex flex-row items-center justify-between ${disabled ? "opacity-35" : ""}`}
        >
          <View className="flex flex-col justify-start flex-1 gap-2">
            <Body className={`${danger ? "text-red" : ""}`}>
              {menuItemValue}
            </Body>
          </View>
          <ChevronRight className="text-dark-grey" />
        </TouchableOpacity>
      </Link>
    </View>
  )
}

interface MenuItemExternalLinkProps {
  menuItemValue: string
  href: string
}

const MenuItemExternalLink: React.FC<MenuItemExternalLinkProps> = ({
  menuItemValue,
  href,
}: MenuItemExternalLinkProps) => {
  return (
    <View className="px-5 py-6 border-b border-light-grey">
      <TouchableOpacity
        testID={`menu-item-${menuItemValue}`}
        className="flex flex-row items-center justify-between"
        onPress={() => {
          void Linking.openURL(href)
        }}
      >
        <View className="flex flex-col justify-start flex-1 gap-2">
          <Body>{menuItemValue}</Body>
        </View>
        <ChevronRight className="text-dark-grey" />
      </TouchableOpacity>
    </View>
  )
}

const LogOutMenuItem = () => {
  const { logout } = useLogout()
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const onShow = useCallback(() => {
    // TODO segment
    bottomSheetModalRef.current?.present()
  }, [])

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
      />
    ),
    []
  )

  return (
    <View className="px-5 py-6 border-b border-light-grey">
      <TouchableOpacity
        testID="logout-button"
        className="flex flex-row items-center justify-between"
        onPress={onShow}
      >
        <View className="flex flex-col justify-start flex-1 gap-2">
          <Body>Log Out</Body>
        </View>
        <ChevronRight className="text-dark-grey" />
      </TouchableOpacity>
      <BottomSheetModal
        ref={bottomSheetModalRef}
        backdropComponent={renderBackdrop}
        index={0}
        backgroundStyle={{ backgroundColor: BFColors.midnightBlue }}
        handleIndicatorStyle={{ backgroundColor: BFColors.calmBlue }}
        enableDynamicSizing={true}
      >
        <BottomSheetView>
          <View className="flex flex-col items-center justify-start py-10 gap-14">
            <H2 className="m-auto text-2xl text-center text-white max-w-[245px] shrink-0 ">
              Are you sure you want to log out?
            </H2>
            <View className="w-3/4 h-14 mx-auto">
              <PrimaryButton
                testID="logout-confirm-button"
                title="Yes"
                onPress={() => {
                  void logout()
                  bottomSheetModalRef.current?.dismiss()
                }}
              />
            </View>
          </View>
        </BottomSheetView>
      </BottomSheetModal>
    </View>
  )
}

const DeleteAccountMenuItem = () => {
  const deleteUserAccountMutation = useDeleteUserAccountMutation()
  const { logout } = useLogout()
  const { data: userData } = useUser()

  const [confirmationText, setConfirmationText] = useState<
    string | null | undefined
  >(undefined)
  const [confirmationErrorText, setConfirmationErrorText] = useState<
    string | null | undefined
  >(undefined)
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)

  const currentUserId = userData?.me?.id ? String(userData.me.id) : null

  const onShow = useCallback(() => {
    // TODO segment
    bottomSheetModalRef.current?.present()
  }, [])

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
      />
    ),
    []
  )

  return (
    <View className="px-5 py-6 border-b border-light-grey">
      <TouchableOpacity
        testID="delete-account-button"
        className="flex flex-row items-center justify-between"
        onPress={onShow}
      >
        <View className="flex flex-col justify-start flex-1 gap-2">
          <Body>Delete Account</Body>
        </View>
        <ChevronRight className="text-dark-grey" />
      </TouchableOpacity>
      <BottomSheetModal
        ref={bottomSheetModalRef}
        backdropComponent={renderBackdrop}
        backgroundStyle={{ backgroundColor: BFColors.midnightBlue }}
        handleIndicatorStyle={{ backgroundColor: BFColors.calmBlue }}
        enableDynamicSizing={true}
      >
        <BottomSheetView>
          <View className="flex flex-col items-center justify-start py-10">
            <H3 className="m-auto text-center text-white max-w-[245px] shrink-0 pb-5">
              Are you sure you want to delete your account?
            </H3>
            <Subtitle className="m-auto text-center text-teal max-w-[245px] shrink-0 pb-5">
              All of your data including Loan Applications and Saved Properties
              will be removed.
            </Subtitle>
            <View className="w-3/4 mb-10 mx-auto">
              <FeedbackTextInput
                testID="delete-account-text-input"
                isBottomSheetInput={true}
                placeholder={"Type `delete` to confirm"}
                keyboardType="default"
                onChange={(val: string | null | undefined) => {
                  setConfirmationErrorText(null)
                  setConfirmationText(val)
                }}
                error={confirmationErrorText}
                value={confirmationText || undefined}
              />
            </View>
            <View className="w-3/4 mb-10 mx-auto">
              <PrimaryButton
                testID="delete-account-confirm-button"
                title="Yes"
                disabled={confirmationText !== "delete"}
                onPress={() => {
                  if (currentUserId && confirmationText === "delete") {
                    deleteUserAccountMutation.mutate(
                      {
                        userId: currentUserId,
                      },
                      {
                        onSuccess: () => {
                          void logout()
                          bottomSheetModalRef.current?.dismiss()
                        },
                        onError: () => {
                          void logout()
                          bottomSheetModalRef.current?.dismiss()
                        },
                      }
                    )
                  } else {
                    setConfirmationErrorText("Type delete to confirm.")
                  }
                }}
              />
            </View>
          </View>
        </BottomSheetView>
      </BottomSheetModal>
    </View>
  )
}

export default function AccountIndexScreen() {
  // const tabHeight = useBottomTabBarHeight()

  useViewedCallback(() => {
    setStatusBarStyle("dark")
    AccountEvents.accountIndexViewed()
  })

  return (
    <BFScreen
      headerSticky={false}
      dark={false}
      noBackButton={false}
      bottomInsets={true}
      header={
        <View className="flex flex-col mt-6 p-6 border-b shrink-0 border-light-grey justify-between">
          <H2 className={`text-midnight-blue`}>Account</H2>
        </View>
      }
    >
      <View className="flex-1 flex-col justify-start">
        <MenuItemLink
          href={"/account/personal-info"}
          menuItemValue="Edit Personal Info"
        />
        <MenuItemExternalLink
          href={`${config.DASHBOARD_URL}/rewards/?token=${$authToken.get()}`}
          menuItemValue="Rewards"
        />
        <MenuItemExternalLink
          href={"https://dobackflip.com/privacy/"}
          menuItemValue="Privacy Policy"
        />
        <LogOutMenuItem />
        <DeleteAccountMenuItem />
        {Updates.channel !== "production" ? (
          <MenuItemLink href={"/account/debug"} menuItemValue="Debug" />
        ) : null}
      </View>
      <View
        className="absolute bottom-3 w-full"
        // style={{ paddingBottom: isWeb ? 10 : tabHeight }}
      >
        <Small className="text-center text-dark-grey">
          {`v${ExpoConstants.expoConfig?.version}`}
          {ExpoConstants.platform?.ios?.buildNumber
            ? ` (${ExpoConstants.platform?.ios?.buildNumber})`
            : ""}
          {"\n"}
          {updateId || "base"}
        </Small>
      </View>
    </BFScreen>
  )
}
